'use client'

import { useState } from 'react'
import { ChevronDown, ChevronUp, ChevronRight, Truck, MapPin, CreditCard, Receipt } from 'lucide-react'
import { formatDateTimeNormal, formatImageURL, formatPrice, getOrderDeliveryBadge, getOrderPaymentBadge, getVendorPrimaryColor, resolveColorHex } from '@/utils/util'
import { PAYMENT_METHOD_LABELS, formatOrderAddress, getCustomerName } from '@/utils/order'
import { navigateUrl } from '@/utils/client-request'
import { usePathname } from 'next/navigation'

const STEP_MAP: Record<string, number> = {
  pending: 0,
  paid: 1,
  ready: 1,
  shipped: 2,
  delivered: 3,
  deliveryFailed: 2,
  canceled: -1,
  refunded: -1,
  partiallyRefunded: -1,
}

const STEPS = ['Захиалсан', 'Багласан', 'Замдаа', 'Хүргэгдсэн']

export default function OrderCard({ order, vendor }: { order: any, vendor?: any }) {
  const [open, setOpen] = useState(false)
  const pathname = usePathname()
  const primaryHex = resolveColorHex(getVendorPrimaryColor(vendor))
  const detailUrl = navigateUrl(`/order/${order.customId}`, pathname)
  const products: any[] = order?.products || []
  const step = STEP_MAP[order?.status] ?? 0
  const isCanceled = step === -1
  const itemCount = products.reduce((acc, p) => acc + (p?.quantity || 1), 0)

  return (
    <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
      <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-center border-b border-border bg-zinc-50/40">
        <div className="flex items-center gap-3 flex-wrap min-w-0">
          {getOrderDeliveryBadge(order)}
          {getOrderPaymentBadge(order)}
          <div className="text-sm text-muted-foreground">
            <span className="tabular-nums">#{order?.customId}</span>
            {order?.createdAt && <> · <span>{formatDateTimeNormal(order.createdAt)}</span></>}
            {itemCount > 0 && <> · <span className="tabular-nums">{itemCount} бараа</span></>}
          </div>
        </div>
        <div className="flex items-center gap-3 justify-self-end">
          <div className="text-right">
            <div className="text-[11px] text-muted-foreground">Нийт</div>
            <div className="text-sm font-semibold tabular-nums">{formatPrice(order?.amount)}</div>
          </div>
          <button onClick={() => setOpen(o => !o)} className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-border hover:bg-accent transition">
            {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      <div className="p-5 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-5 items-start">
        <div className="space-y-3 min-w-0">
          {products.slice(0, open ? products.length : 2).map((it: any, i: number) => {
            const image = it?.variant?.images?.[0] || 'placeholder.png'
            const name = it?.name || it?.product?.name || it?.variant?.name
            const qty = it?.quantity || 1
            const price = it?.variant?.sale || it?.variant?.price || 0
            return (
              <div key={i} className="flex items-center gap-3 min-w-0">
                <div className="w-14 h-14 shrink-0 rounded-md overflow-hidden bg-muted">
                  <img src={formatImageURL(image)} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{name}</div>
                  {it?.variant?.name && <div className="text-xs text-muted-foreground mt-0.5 truncate">{it.variant.name} · <span className="tabular-nums">{qty} ширхэг</span></div>}
                </div>
                <div className="ml-auto text-sm tabular-nums whitespace-nowrap pl-3">{formatPrice(price * qty)}</div>
              </div>
            )
          })}
          {!open && products.length > 2 && (
            <button onClick={() => setOpen(true)} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
              +{products.length - 2} илүү бараа <ChevronDown className="w-3 h-3" />
            </button>
          )}

          {!isCanceled && (
            <div className="mt-4 pt-4 border-t border-border">
              <div className="flex items-start gap-1">
                {STEPS.map((s, i) => (
                  <div key={s} className="flex-1">
                    <div className="h-1.5 rounded-full" style={i <= step ? { background: primaryHex } : { background: '#f4f4f5' }} />
                    <div className={`mt-1.5 text-[11px] tracking-tight ${i <= step ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>{s}</div>
                  </div>
                ))}
              </div>
              {order?.deliveryDate && (
                <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Truck className="w-3 h-3" />{formatDateTimeNormal(order.deliveryDate)}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2 lg:w-44 lg:border-l lg:border-border lg:pl-5">
          <a href={detailUrl} className="h-9 px-3 rounded-md text-white text-sm font-medium inline-flex items-center justify-center gap-1.5 hover:opacity-95 transition" style={{ background: primaryHex }}>
            Дэлгэрэнгүй <ChevronRight className="w-3.5 h-3.5" />
          </a>
          {order?.status === 'shipped' && (
            <button className="h-9 px-3 rounded-md border border-border text-sm inline-flex items-center justify-center gap-1.5 hover:bg-accent transition">
              <MapPin className="w-3.5 h-3.5" />Хүргэлт хянах
            </button>
          )}
        </div>
      </div>

      {open && (
        <div className="border-t border-border bg-zinc-50/30 px-5 py-4 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <DetailBlock title="Хүргэлтийн хаяг" icon={MapPin}>
            {(() => {
              const name = getCustomerName(order)
              const addressText = formatOrderAddress(order?.address, order?.channel)
              if (!name && !addressText) return <div className="text-muted-foreground">—</div>
              return (
                <>
                  {name && <div>{name}</div>}
                  {addressText && <div className="text-muted-foreground wrap-break-word">{addressText}</div>}
                  {order?.phone && <div className="text-muted-foreground tabular-nums">{order.phone}{order?.additionalPhone ? ` · ${order.additionalPhone}` : ''}</div>}
                </>
              )
            })()}
          </DetailBlock>
          <DetailBlock title="Төлбөр" icon={CreditCard}>
            <div>{PAYMENT_METHOD_LABELS[order?.paymentMethod] || PAYMENT_METHOD_LABELS.transfer}</div>
            <div className="text-muted-foreground tabular-nums">Нийт: {formatPrice(order?.amount)}</div>
          </DetailBlock>
          <DetailBlock title="Захиалга" icon={Receipt}>
            <div className="tabular-nums">#{order?.customId}</div>
            {order?.createdAt && <div className="text-muted-foreground">{formatDateTimeNormal(order.createdAt)}</div>}
          </DetailBlock>
        </div>
      )}
    </div>
  )
}

function DetailBlock({ title, icon: I, children }: { title: string, icon: any, children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        <I className="w-3 h-3" />{title}
      </div>
      <div className="mt-1.5 space-y-0.5 text-sm">{children}</div>
    </div>
  )
}
